import { CancellationToken } from 'infra/cancellation'
import { PrefilledDraftInput } from 'jobs/creation/PrefilledDraftInput'
import { MonoTypeOperatorFunction, Observable, Subject } from 'rxjs'
import {
  distinctUntilChanged,
  first,
  repeat,
  switchMap,
  takeWhile,
} from 'rxjs/operators'
import { shareBehavior, whileActive } from 'utils/rx-util'
import Job from '../Job'
import { DraftChange } from './DraftChange'
import { StepContainer } from './OutlineViewModel'

/**
 * An object that manages a slice of the draft job state.
 */
export abstract class DraftStateSlice {
  /**
   * Used to emit when the slice is ready (always happens after at
   * least one `prepare`).
   *
   * @private
   */
  private readonly readySubject = new Subject()

  /**
   * Emits `true` when the slice is ready, `false` when not.
   *
   * This is used to defer emissions of observables until the slice
   * has been fully prepared. For example, we don't want the `selectedLegendVersion$`
   * stream to emit anything until we have prepared the slice (loaded the legend).
   *
   * This is what lets other slices have their queries and preparations depend on
   * observables indirectly from other slices.
   *
   * @protected
   */
  protected readonly ready$ = this.readySubject.pipe(
    // Only take the first one; that means if we become
    // ready again while active, we don't trigger the behavior more than once.
    first(),
    // Once we are ready, switch to `isActive$`.
    switchMap(() =>
      this.isActive$.pipe(
        // Complete this observable once we deactivate (`isActive$` emits `false`).
        // There's a possibility that the first emission is `false`,
        // which means it ends after that.
        // That is ok and intentional. We never emit `ready`
        // BEFORE being active in the happy path, but its possible that
        // we deactivate before `ready` emits, so this handles that race.
        takeWhile((v) => v, true),
      ),
    ),
    // Start from the beginning; wait for the next `ready`.
    repeat(),
    shareBehavior(false),
    distinctUntilChanged(),
  )

  /**
   * Getter for step containers.
   */
  abstract readonly stepContainers: StepContainer[]

  /**
   * Whenever the state changes, emits a notification.
   *
   * This is optional.
   */
  abstract readonly changes$?: Observable<DraftChange>

  /**
   * Constructor.
   *
   * @param isActive$ Observable for when the slice is actively being used.
   */
  protected constructor(protected isActive$: Observable<boolean>) {}

  /**
   * Used to prepare the slice by loading the data for an existing draft
   * or pre-fetching stuff for creating a new draft.
   *
   * The idea is that when loading an existing draft, we might not have to fetch
   * any heavy lists since the draft might already have a selection.
   *
   * It gets called at most once per view lifecycle.
   *
   * Example: the Legend Selector will load **just the Legend** for an existing
   * draft, while it starts loading the Legend list for a new one.
   *
   * @param context
   * @param ct
   */
  abstract prepare(
    context: PrepareContext,
    ct: CancellationToken,
  ): Promise<void>

  /**
   * Must be called after `prepare` and when reviving.
   */
  ready() {
    this.readySubject.next(undefined)
  }

  /**
   * Operator used for starting and stopping a subscription based on
   * active status.
   *
   * @protected
   */
  protected whileActive<T>(): MonoTypeOperatorFunction<T> {
    return whileActive(this.isActive$)
  }

  /**
   * Like `whileActive` but does not start until `prepare` has run to completion
   * at least once. Once deactivated, we wait for another `prepare`.
   *
   * @protected
   */
  protected whileReady<T>(): MonoTypeOperatorFunction<T> {
    return whileActive(this.ready$)
  }
}

/**
 * Context passed to `prepare`.
 */
export type PrepareContext = NewDraftContext | EditDraftContext

/**
 * Starting a new Draft. May contain some preselected stuff.
 */
export interface NewDraftContext {
  type: 'NewDraft'
  prefilledDraft: PrefilledDraftInput
}

/**
 * Editing an existing Draft.
 */
export interface EditDraftContext {
  type: 'EditDraft'
  job: Job
}
